import { useMemo, useState } from 'react';

import { Alert, Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ReactApexChart from 'react-apexcharts';
import Subtitle from 'src/components/Subtitle';
import { convertValue } from 'src/utils/number';

import type { MetaCarteirasFicFidcsWalletHistoryItem } from 'src/services/meta-carteiras-fic-fidcs';
type WalletHistoryPeriod = '6_months' | '12_months' | 'all_history';
type WalletHistoryPresentation = 'standard' | 'complete';

interface FicFidcWalletHistoryChartProps {
  history: MetaCarteirasFicFidcsWalletHistoryItem[];
  isLoading?: boolean;
  isError?: boolean;
  presentation?: WalletHistoryPresentation;
}

const PERIOD_OPTIONS: Array<{ value: WalletHistoryPeriod; label: string }> = [
  { value: '6_months', label: '6 meses' },
  { value: '12_months', label: '12 meses' },
  { value: 'all_history', label: 'Ótimo' },
];

type ChartLabelFormatterContext = {
  dataPointIndex?: number;
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 2,
  }).format(value);

const formatAxisValue = (value: number) => {
  if (value == null || Number.isNaN(Number(value))) return '';

  return String(
    convertValue(Number(value), '', true, 'currency', 'BRL', 2, 2),
  ).trim();
};

const formatCompetence = (competence: string | number) => {
  const competenceValue = String(competence);
  const [year, month] = competenceValue.split('-');
  const monthIndex = Number(month) - 1;

  if (!year || monthIndex < 0 || monthIndex > 11) return competenceValue;

  return `${String(monthIndex + 1).padStart(2, '0')}/${year}`;
};

export const formatWalletHistoryMonthLabel = (competence: string | number) => {
  const competenceValue = String(competence);
  const [year, month] = competenceValue.split('-');
  const monthIndex = Number(month) - 1;
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];

  if (!year || months[monthIndex] === undefined) return competenceValue;

  return `${months[monthIndex]}/${year.slice(-2)}`;
};

const getPeriodStart = (latestCompetence: string, months: number) => {
  const [year, month] = latestCompetence.split('-').map(Number);
  const date = new Date(year, month - months, 1);

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
};

export function getWalletHistoryForPeriod(
  history: MetaCarteirasFicFidcsWalletHistoryItem[],
  period: WalletHistoryPeriod,
  competencyDate?: string,
) {
  const orderedHistory = [...history].sort((a, b) =>
    a.competence.localeCompare(b.competence),
  );

  if (period === 'all_history' || !orderedHistory.length) return orderedHistory;

  const months = period === '6_months' ? 6 : 12;
  const latestCompetence = orderedHistory[orderedHistory.length - 1].competence;
  const periodEnd = competencyDate?.slice(0, 7) || latestCompetence;
  const periodStart = getPeriodStart(periodEnd, months);

  return orderedHistory.filter(
    ({ competence }) => competence >= periodStart && competence <= periodEnd,
  );
}

export default function FicFidcWalletHistoryChart({
  history,
  isLoading = false,
  isError = false,
  presentation = 'standard',
}: FicFidcWalletHistoryChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isComplete = presentation === 'complete';
  const [selectedPeriod, setSelectedPeriod] =
    useState<WalletHistoryPeriod>('12_months');
  const visibleHistory = useMemo(
    () =>
      getWalletHistoryForPeriod(
        history,
        isComplete ? 'all_history' : selectedPeriod,
      ),
    [history, isComplete, selectedPeriod],
  );
  const formatHistoryCompetence = (
    value: string | number,
    _timestamp?: number,
    opts?: ChartLabelFormatterContext,
  ) =>
    formatCompetence(
      visibleHistory[opts?.dataPointIndex ?? -1]?.competence ?? value,
    );
  const formatTooltipHistoryCompetence = (
    value: string | number,
    opts?: ChartLabelFormatterContext,
  ) => formatHistoryCompetence(value, undefined, opts);
  const formatCompleteMonth = (
    value: string | number,
    _timestamp?: number,
    opts?: ChartLabelFormatterContext,
  ) => {
    const index = opts?.dataPointIndex ?? -1;
    const lastIndex = visibleHistory.length - 1;
    const labelInterval = Math.max(1, Math.ceil(visibleHistory.length / 5));

    if (
      isMobile &&
      index >= 0 &&
      index !== lastIndex &&
      index % labelInterval
    ) {
      return '';
    }

    return formatWalletHistoryMonthLabel(
      visibleHistory[index]?.competence ?? value,
    );
  };

  if (isLoading) {
    return isComplete ? (
      <Skeleton
        data-testid="fic-fidc-complete-equity-loading"
        height={isMobile ? 320 : 390}
        variant="rectangular"
      />
    ) : (
      <Box mt={3}>
        <Subtitle title="Evolução Histórica da Carteira" />
        <Skeleton
          height={isMobile ? 360 : 520}
          aria-label="Carregando histórico da carteira"
        />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box mt={isComplete ? 0 : 3}>
        {!isComplete && <Subtitle title="Evolução Histórica da Carteira" />}
        <Alert severity="error" variant="outlined" role="alert">
          Não foi possível carregar o histórico da carteira.
        </Alert>
      </Box>
    );
  }

  if (isComplete && visibleHistory.length === 0) {
    return (
      <Alert severity="info" variant="outlined" role="status">
        Não há histórico de patrimônio para a competência selecionada.
      </Alert>
    );
  }

  return (
    <Box mt={isComplete ? 0 : 3} p={isComplete ? 0 : 1}>
      {!isComplete && <Subtitle title="Evolução Histórica da Carteira" />}
      <Box px={isComplete ? 0 : { xs: 0, md: 4 }}>
        <Box width="100%">
          {!isComplete && (
            <Box
              display="flex"
              justifyContent="flex-end"
              gap={1}
              py={2}
              aria-label="Selecionar período do gráfico">
              {PERIOD_OPTIONS.map((option) => {
                const isSelected = option.value === selectedPeriod;

                return (
                  <Typography
                    key={option.value}
                    component="button"
                    type="button"
                    variant="button"
                    aria-pressed={isSelected}
                    data-testid={`period-selector-wallet-history-${option.value}`}
                    sx={{
                      cursor: 'pointer',
                      background: 'none',
                      p: 0,
                      minWidth: 'unset',
                      minHeight: 'unset',
                      border: 'none',
                      borderRadius: 0,
                      borderBottom: '2px solid',
                      borderColor: isSelected ? '#FF8211' : 'transparent',
                      color: 'text.primary',
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    onClick={() => setSelectedPeriod(option.value)}>
                    {option.label}
                  </Typography>
                );
              })}
            </Box>
          )}
          <ReactApexChart
            type={isComplete ? 'area' : 'bar'}
            height={isComplete ? (isMobile ? 320 : 390) : isMobile ? 360 : 520}
            series={[
              {
                name: isComplete ? 'Patrimônio líquido' : 'Carteira',
                data: visibleHistory.map(
                  ({ competence, totalVlMercPosFinal }) => ({
                    x: competence,
                    y: totalVlMercPosFinal,
                  }),
                ),
              },
            ]}
            options={{
              chart: {
                type: isComplete ? 'area' : 'bar',
                toolbar: { show: false },
                zoom: { enabled: false },
                width: '100%',
                foreColor: theme.palette.text.secondary,
              },
              colors: [theme.palette.primary.main],
              dataLabels: { enabled: false },
              ...(isComplete
                ? {
                    stroke: {
                      curve: 'straight' as const,
                      width: 3,
                    },
                    markers: {
                      size: 4,
                      colors: [theme.palette.primary.main],
                      strokeColors: theme.palette.background.paper,
                      strokeWidth: 2,
                      hover: { size: 6 },
                    },
                    fill: {
                      type: 'gradient' as const,
                      gradient: {
                        shade: 'light' as const,
                        type: 'vertical' as const,
                        shadeIntensity: 0.25,
                        opacityFrom: 0.38,
                        opacityTo: 0.04,
                        stops: [0, 85, 100],
                      },
                    },
                  }
                : {
                    plotOptions: {
                      bar: {
                        columnWidth: '55%',
                        borderRadius: 2,
                      },
                    },
                  }),
              grid: {
                borderColor: theme.palette.divider,
                strokeDashArray: isComplete ? 4 : 0,
                padding: { right: 0 },
              },
              xaxis: {
                type: 'category',
                labels: {
                  formatter: isComplete
                    ? formatCompleteMonth
                    : formatCompetence,
                  rotate: isComplete && !isMobile ? 0 : -35,
                  rotateAlways: false,
                  hideOverlappingLabels: true,
                  style: {
                    colors: isComplete ? theme.palette.text.secondary : '#666',
                    fontSize: isComplete ? '11px' : '12px',
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 400,
                  },
                },
                tooltip: { formatter: formatTooltipHistoryCompetence },
              },
              yaxis: {
                labels: {
                  formatter: formatAxisValue,
                  style: {
                    colors: [
                      isComplete ? theme.palette.text.secondary : '#666',
                    ],
                    fontSize: '12px',
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 400,
                  },
                },
              },
              tooltip: {
                x: { formatter: formatTooltipHistoryCompetence },
                y: { formatter: formatCurrency },
              },
              noData: {
                text: 'Sem dados históricos da carteira.',
                align: 'center',
                verticalAlign: 'middle',
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
